import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { faFolder } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetArticleResponseBody } from '~/lib/api/modules/Articles';
import type {
  IArticleFormSubmitAction,
  IArticleFormValues,
} from './ArticleForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useCreateArticleMutation } from '~/lib/api/modules/Articles/hooks/useCreateArticleMutation';
import { usePublishArticleMutation } from '~/lib/api/modules/Articles/hooks/usePublishArticleMutation';
import { ArticleForm } from './ArticleForm';

export type ICreateArticleFormProps = IContainerProps & {
  onSuccess?: (outputData: unknown, inputValues: IArticleFormValues) => void;
  cancelUrl?: string;
  appId: string;
  parentCategoryId?: string;
  onArticleChange?: (article: IApiGetArticleResponseBody) => void;
};

export const CreateArticleForm: React.FC<ICreateArticleFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, parentCategoryId, onArticleChange } =
    props;
  const { t } = useTranslation();

  const [submittingAction, setSubmittingAction] = useState<
    IArticleFormSubmitAction | undefined
  >();
  const createArticleMutation = useCreateArticleMutation();
  const publishArticleMutation = usePublishArticleMutation();

  const handleSubmit: SubmitHandler<IArticleFormValues> = async (
    values: IArticleFormValues,
    _?: React.BaseSyntheticEvent,
    action?: IArticleFormSubmitAction,
  ): Promise<void> => {
    if (submitting) {
      return;
    }

    setSubmittingAction(action);

    const actions = values.actionButtonEnabled
      ? [
          {
            title: values.actionButtonLabel ?? '',
            url: `${values.actionButtonPrefix ?? ''}${values.actionButtonTarget ?? ''}`,
          },
        ]
      : undefined;

    const badges = values.userBadges?.map((id) => id);
    const badgesAllow = values.userBadges?.length
      ? values.requireAllUserBadges
        ? 'all'
        : 'any'
      : undefined;

    const mainPictureIds = values.mainMedias
      ?.filter(({ type }) => type === 'picture')
      .map(({ id }) => id);
    const galleryPictureIds = values.galleryPictures?.map(({ id }) => id);
    const pictureIds = [
      ...(mainPictureIds ?? []),
      ...(galleryPictureIds ?? []),
    ];

    const mainVideoIds = values.mainMedias
      ?.filter(({ type }) => type === 'video')
      .map(({ id }) => id);
    const videoIds = mainVideoIds ?? [];

    const documentIds = values.documents?.map(({ id }) => id);

    return createArticleMutation
      .mutateAsync({
        appId,
        body: {
          actions,
          badges,
          badgesAllow,
          categoriesId: values.categoryIds,
          eventEndDate: values.eventEndDate,
          eventStartDate: values.eventStartDate,
          isWebview: values.type === 'webview',
          md: values.markdown ?? '',
          html: values.html,
          pdfsOpenButton: values.documentsButtonLabel,
          mediaCaptions: values.mainMediaCaption,
          pdfs: documentIds,
          pictures: pictureIds,
          feedPicture: values.feedPictures?.[0]?.id,
          pinned: values.pinned,
          summary: ' ',
          title: values.title ?? '',
          videos: videoIds,
          productId: values.productId,
          isEvent: values.isEvent,
          hideFromFeed: values.hideFromFeed,
          displayOptions: {
            hideComments: values.hideComments,
            hideSocial: values.hideShareButtons,
            fullscreen: values.fullscreen,
            rawContent: values.rawContent,
          },
          isPoll: values.type === 'poll',
        },
      })
      .then(async (data) => {
        if (action === 'publish' || action === 'publishAndNotify') {
          await publishArticleMutation.mutateAsync({
            appId,
            articleId: data.articleId,
            body: {
              draftId: data.draftId,
              date: values.publishAt ?? new Date().toISOString(),
              unpublicationDate: values.unpublishAt,
              ...(action === 'publishAndNotify'
                ? {
                    sendNotifications: true,
                    notificationTitle: values.notificationTitle,
                    notificationContent: values.notificationBody,
                  }
                : undefined),
            },
          });
        }
      })
      .then(() => onSuccess?.(undefined, values))
      .finally(() => setSubmittingAction(undefined));
  };

  const submitting =
    createArticleMutation.isLoading || publishArticleMutation.isLoading;

  return (
    <ContentBlock draft={true} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faFolder} />}>
        {t('CreateArticleForm.create-an-article')}
      </ContentBlock.Title>

      <ArticleForm
        mode="creation"
        appId={appId}
        cancelUrl={cancelUrl}
        defaultValues={{
          categoryIds: parentCategoryId ? [parentCategoryId] : undefined,
          type: 'article',
          // Provide a dummy markdown to initialize the editor.
          markdown: '',
        }}
        onSubmit={handleSubmit}
        submittingAction={submittingAction}
        onArticleChange={onArticleChange}
        canSchedulePublication
      />
    </ContentBlock>
  );
};
